'use client'

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {Account, ChatMessage, Contact} from '@/types'
import {displayName} from '@/lib/utils'
import {apiGet, apiPost} from '@/lib/api'

type UnreadCounts = Record<string, number>

export const APP_MESSAGE_EVENT = 'wechat-desk:message'

export function readUnreadTotal(wxid: string): number {
    // Deprecated — kept for compatibility, actual data comes from state
    void wxid
    return 0
}

function notificationBody(message: ChatMessage): string {
    return message.content.length > 120
        ? message.content.slice(0, 120) + '...'
        : message.content
}

type UseMessageNotificationsInput = {
    account: Account | null
    contacts: Contact[]
    messageNotifications: boolean
    activeConversationWxid: string
    conversationPageActive: boolean
    onOpenConversation: (wxid: string) => void
}

type UseMessageNotificationsResult = {
    unreadCounts: UnreadCounts
    totalUnread: number
    markConversationRead: (conversationWxid: string) => void
    markAllConversationsRead: () => void
    accountUnreadCounts: Record<string, number>
}

export function useMessageNotifications({
    account,
    contacts,
    messageNotifications,
    activeConversationWxid,
    conversationPageActive,
    onOpenConversation,
}: UseMessageNotificationsInput): UseMessageNotificationsResult {
    const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({})
    const skipNextPersistRef = useRef(true)

    const totalUnread = useMemo(
        () => Object.values(unreadCounts).reduce((total, count) => total + count, 0),
        [unreadCounts],
    )

    const configRef = useRef({activeConversationWxid, contacts, conversationPageActive, messageNotifications, onOpenConversation})
    useEffect(() => {
        configRef.current = {activeConversationWxid, contacts, conversationPageActive, messageNotifications, onOpenConversation}
    }, [activeConversationWxid, contacts, conversationPageActive, messageNotifications, onOpenConversation])

    // Load unread counts from server on mount / account change
    useEffect(() => {
        if (!account) {
            setUnreadCounts({})
            return
        }
        skipNextPersistRef.current = true
        apiGet<{success: boolean; counts: UnreadCounts}>(`/unread?wxid=${encodeURIComponent(account.wxid)}`, {silent: true})
            .then((res) => setUnreadCounts(res.counts || {}))
            .catch(() => setUnreadCounts({}))
    }, [account])

    // Persist unread counts to server
    useEffect(() => {
        if (!account) return
        if (skipNextPersistRef.current) {
            skipNextPersistRef.current = false
            return
        }
        apiPost('/unread', {wxid: account.wxid, counts: unreadCounts}, {silent: true}).catch(() => {})
    }, [account, unreadCounts])

    const markConversationRead = useCallback((conversationWxid: string) => {
        if (!conversationWxid) return
        setUnreadCounts((current) => {
            if (!current[conversationWxid]) return current
            const next = {...current}
            delete next[conversationWxid]
            return next
        })
    }, [])

    const markAllConversationsRead = useCallback(() => {
        setUnreadCounts({})
    }, [])

    useEffect(() => {
        if (!conversationPageActive || !activeConversationWxid) return
        markConversationRead(activeConversationWxid)
    }, [activeConversationWxid, conversationPageActive, markConversationRead])

    useEffect(() => {
        if (!account) return
        const source = new EventSource(
            '/api/messages/events?wxid=' + encodeURIComponent(account.wxid),
        )
        const onMessage = (event: MessageEvent) => {
            let payload: {message?: ChatMessage}
            try {
                payload = JSON.parse(event.data) as {message?: ChatMessage}
            } catch {
                return
            }
            const incoming = payload.message
            if (!incoming) return

            window.dispatchEvent(new CustomEvent(APP_MESSAGE_EVENT, {
                detail: incoming,
            }))

            const {activeConversationWxid: active, contacts: cts, conversationPageActive: pageActive, messageNotifications: notif, onOpenConversation: onOpen} = configRef.current
            const fromWxid = incoming.groupSenderWxid || incoming.fromWxid || incoming.conversationWxid
            const outgoing = incoming.direction === 'out' || incoming.fromWxid === account.wxid

            // Skip notification for muted contacts
            const contact = cts.find((ct) => ct.wxid === fromWxid)
            const isMuted = Boolean((contact as Record<string, unknown>)?.muted)

            if (!outgoing && !isMuted && notif && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const title = contact ? displayName(contact) : fromWxid
                try {
                    const notification = new Notification(title, {
                        body: notificationBody(incoming),
                        icon: contact?.avatar || undefined,
                        tag: incoming.conversationWxid,
                    })
                    notification.onclick = () => {
                        window.focus()
                        onOpen(incoming.conversationWxid)
                    }
                } catch {
                    // Notification constructor may fail in some environments
                }
            }

            if (!outgoing && !(pageActive && active === incoming.conversationWxid)) {
                setUnreadCounts((current) => ({
                    ...current,
                    [incoming.conversationWxid]: (current[incoming.conversationWxid] || 0) + 1,
                }))
            }
        }

        source.addEventListener('message', onMessage as EventListener)
        return () => {
            source.removeEventListener('message', onMessage as EventListener)
            source.close()
        }
    }, [account])

    return {unreadCounts, totalUnread, markConversationRead, markAllConversationsRead, accountUnreadCounts: {}}
}
